import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import { useAuth } from '../auth.jsx'
import { ClipCard, ClipModal } from '../components/Clips.jsx'
import { BellIcon, CheckIcon } from '../components/Icons.jsx'
import LiveChat, { ReactionButtons, useFloaters } from '../components/LiveChat.jsx'
import LiveOn from '../components/LiveOn.jsx'
import RecentFeeders from '../components/RecentFeeders.jsx'
import LivePlayer from '../components/LivePlayer.jsx'
import ProgressBar from '../components/ProgressBar.jsx'
import DonateButton from '../components/DonateButton.jsx'
import { DEMO_CONTENT } from '../config.js'
import { enablePush } from '../push.js'
import { pickI18n } from '../i18n'
import { isToday, parseTs, pct, timeAgo } from '../lib.js'
import { ErrorState, Loading } from './states.jsx'

const DEMO_MOMENTS = [
  { src: '/demo/moment-1.jpg', time: '08:12' },
  { src: '/demo/moment-2.jpg', time: '10:40' },
  { src: '/demo/moment-3.jpg', time: '13:05' },
  { src: '/demo/moment-4.jpg', time: '15:30' },
]

function MiniStat({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-2xl bg-surface px-4 py-3.5">
      <span className="text-[13px] font-bold text-subtle">{label}</span>
      <span className="text-xl font-extrabold">{value ?? '—'}</span>
    </div>
  )
}

/** /live → the first online feeder (or the first one). */
export function LiveRedirect() {
  const { t } = useTranslation()
  const { data, error, loading } = useApi(() => api.feeders())
  if (loading) return <Loading />
  if (error) return <ErrorState />
  const target = data.find((f) => f.is_online) ?? data[0]
  if (!target) return <p className="px-8 py-24 text-center text-lg text-muted">{t('live.noLiveFeeders')}</p>
  return <Navigate to={`/feeders/${target.slug}`} replace />
}

export default function LivePage() {
  const { slug } = useParams()
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const { user, refreshProfile } = useAuth()
  const floaters = useFloaters()
  const navigate = useNavigate()
  // ?clip=<id>: shared links and web pushes open that clip over the page.
  const [params, setParams] = useSearchParams()
  const openClip = params.get('clip')
  const showClip = (id) =>
    setParams((p) => {
      if (id) p.set('clip', id)
      else p.delete('clip')
      return p
    })
  const [message, setMessage] = useState(null)
  const [busy, setBusy] = useState(false)

  const feeder = useApi(() => api.feeder(slug), [slug])
  const events = useApi(() => api.events(slug), [slug])
  const cats = useApi(() => api.cats(slug), [slug])
  const moments = useApi(() => api.moments(slug), [slug])
  const clips = useApi(() => api.feederClips(slug), [slug, user?.uid])
  const follows = useApi(() => (user ? api.follows() : Promise.resolve([])), [user?.uid])

  if (feeder.loading) return <Loading />
  if (feeder.error) return <ErrorState notFound={feeder.error.status === 404} />

  const f = feeder.data
  const tel = f.last_telemetry ?? {}
  const meals = (events.data ?? []).filter((e) => e.type === 'dispensed')
  const mealsToday = meals.filter((e) => isToday(e.ts)).length
  const follow = follows.data?.find((x) => x.feeder_id === f.id)
  const poster = f.photo_url ?? (DEMO_CONTENT ? '/demo/feed-cam1.jpg' : null)
  const realMoments = (moments.data ?? []).map((m) => ({
    src: m.image_url,
    title: t('events.dispensed'),
    time: parseTs(m.ts).toLocaleTimeString(lang, {
      hour: '2-digit',
      minute: '2-digit',
    }),
  }))
  const shownMoments =
    realMoments.length > 0 || !DEMO_CONTENT
      ? realMoments
      : DEMO_MOMENTS.map((m, i) => ({ ...m, title: t(`demoMoments.${i}`) }))

  const feedOne = async () => {
    if (!user) return navigate('/login', { state: { from: `/feeders/${slug}` } })
    setBusy(true)
    setMessage(null)
    try {
      await api.feed(slug)
      setMessage({ ok: true, text: t('live.feedSent') })
      setTimeout(events.reload, 12000)
      setTimeout(refreshProfile, 15000) // meal count / new title once the feeder confirms
      setTimeout(clips.reload, 60000) // the meal clip: 25 s of video + encoding
    } catch (e) {
      setMessage({ ok: false, text: e.message })
    } finally {
      setBusy(false)
    }
  }

  const toggleFollow = async () => {
    if (!user) return navigate('/login', { state: { from: `/feeders/${slug}` } })
    try {
      await (follow ? api.unfollow(follow.id) : api.follow({ feeder_id: f.id }))
      follows.reload()
      if (!follow) enablePush().catch(() => {})
    } catch (e) {
      setMessage({ ok: false, text: e.message })
    }
  }

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-7 px-4 pt-8 pb-[72px] sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-[clamp(30px,3.5vw,42px)] font-black tracking-[-1px]">{pickI18n(f.name, lang)}</h1>
          <span className="flex items-center gap-2 text-base font-bold text-muted">
            <span className={`size-2 rounded-full ${f.is_online ? 'bg-teal-soft' : 'bg-faint'}`} />
            {f.is_online ? t('common.online') : t('common.offline')}
            {f.city && ` · ${f.city}`}
          </span>
        </div>
        <button
          type="button"
          onClick={toggleFollow}
          aria-pressed={Boolean(follow)}
          className={`flex cursor-pointer items-center gap-2 rounded-full border-[1.5px] px-5 py-3 text-base font-extrabold transition ${
            follow ? 'border-teal bg-teal text-on-teal' : 'border-edge bg-surface text-ink hover:border-ink'
          }`}
        >
          {follow ? <CheckIcon /> : <BellIcon />}
          {follow ? t('live.following') : t('live.follow')}
        </button>
      </div>

      <div className="flex flex-wrap items-stretch gap-6">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-3.5">
          <div className="relative">
          <LivePlayer
            src={f.is_online ? f.stream_url : null}
            poster={poster}
            alt={pickI18n(f.name, lang)}
            camLabel={t('live.camLabel')}
            viewers={f.viewers}
          />
          {floaters.layer}
          </div>
          <ReactionButtons slug={f.slug} onReact={(kind) => floaters.add(kind)} />
          <LiveOn links={f.live_on} />
          <div className="flex flex-wrap gap-2.5" role="group" aria-label={t('live.cameras')}>
            <button
              type="button"
              aria-pressed="true"
              className="rounded-full bg-ink px-[18px] py-2.5 text-[15px] font-extrabold text-on-ink"
            >
              {t('live.camBowl')}
            </button>
            <button
              type="button"
              disabled
              className="rounded-full border-[1.5px] border-dashed border-edge px-[18px] py-2.5 text-[15px] font-bold text-faint"
            >
              {t('live.cam2')}
            </button>
          </div>
        </div>

        <aside className="flex flex-[1_1_320px] flex-col gap-[22px] rounded-3xl bg-paper p-7">
          <span className="text-sm font-extrabold tracking-[1.5px] text-subtle uppercase">{t('live.rightNow')}</span>
          <div className="flex flex-col gap-2.5">
            <div className="flex justify-between text-[15px] font-bold">
              <span>{t('live.food')}</span>
              <span>{pct(tel.food_level_percent) ?? '—'}</span>
            </div>
            <ProgressBar value={tel.food_level_percent} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MiniStat label={t('live.lastMeal')} value={meals[0] ? timeAgo(meals[0].ts, lang) : t('live.never')} />
            <MiniStat label={t('live.mealsToday')} value={events.loading ? null : mealsToday} />
            <MiniStat label={t('live.battery')} value={pct(tel.battery_percent)} />
            <MiniStat
              label={t('live.temperature')}
              value={tel.temperature_c == null ? null : `${Math.round(tel.temperature_c)}°C`}
            />
          </div>
          <div className="mt-auto flex flex-col gap-2.5">
            <button
              type="button"
              onClick={feedOne}
              disabled={busy}
              className="flex cursor-pointer justify-center rounded-2xl bg-teal p-4 text-lg font-extrabold text-on-teal transition hover:bg-teal-dark disabled:opacity-60"
            >
              {user ? t('live.feedOne') : t('live.loginToFeed')}
            </button>
            <DonateButton
              kind="monthly"
              feeder={f.slug}
              className="flex justify-center rounded-2xl border-[1.5px] border-edge bg-surface p-4 text-[17px] font-extrabold text-ink transition hover:border-ink"
            >
              {t('live.subscribe')}
            </DonateButton>
            <RecentFeeders slug={f.slug} version={message?.ok ? 1 : 0} />
            <p
              role="status"
              className={`text-center text-sm leading-snug ${
                message ? (message.ok ? 'font-bold text-teal' : 'font-bold text-live') : 'text-subtle'
              }`}
            >
              {message?.text ?? t('live.hint')}
            </p>
          </div>
        </aside>
      </div>

      <LiveChat
        key={slug}
        slug={slug}
        onReactions={(fresh) => Object.entries(fresh).forEach(([kind, n]) => n && floaters.add(kind, n))}
      />

      {clips.data?.length > 0 && (
        <section className="flex flex-col gap-[18px] pt-6">
          <h2 className="text-[28px] font-black tracking-[-0.6px]">{t('cabinet.feederVideos')}</h2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(240px,100%),1fr))] gap-4">
            {clips.data.map((c) => (
              <ClipCard key={c.id} clip={c} showFeeder={false} onOpen={(x) => showClip(String(x.id))} />
            ))}
          </div>
        </section>
      )}

      {shownMoments.length > 0 && (
        <section className="flex flex-col gap-[18px] pt-6">
          <h2 className="text-[28px] font-black tracking-[-0.6px]">{t('live.moments')}</h2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(240px,100%),1fr))] gap-4">
            {shownMoments.map((m) => (
              <figure key={m.src} className="flex flex-col gap-2">
                <img src={m.src} alt={m.title} className="aspect-[16/10] w-full rounded-2xl object-cover" />
                <figcaption className="flex justify-between text-[15px]">
                  <span className="font-extrabold">{m.title}</span>
                  <span className="font-bold text-subtle">{m.time}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {cats.data?.length > 0 && (
        <section className="flex flex-col gap-[18px] pt-4">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="text-[28px] font-black tracking-[-0.6px]">{t('live.catsHere')}</h2>
            <Link to="/cats" className="text-base font-extrabold text-teal no-underline hover:text-teal-dark">
              {t('live.allCats')}
            </Link>
          </div>
          <div className="flex flex-wrap gap-5">
            {cats.data.map((c) => (
              <Link key={c.id} to="/cats" className="flex w-24 flex-col items-center gap-2 text-ink no-underline">
                <img
                  src={c.photo_url ?? '/brand/urbanpurr-app-icon.svg'}
                  alt=""
                  className="size-20 rounded-full object-cover"
                />
                <span className="text-[15px] font-extrabold">{c.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
      {openClip ? <ClipModal key={openClip} clipId={openClip} onClose={() => showClip(null)} /> : null}
    </main>
  )
}
